import { useEffect } from 'react';
import useSWR from 'swr';
import { useQueryParams } from './use-query-params';
import { UserType } from '../types/common';
import { verifyCognitoJwt } from '@components/common/auth/verify-cognito-jwt';
import { isHeliosLegacyTokenExpired } from '@components/common/auth/is-helios-legacy-token-expired';
import { verifyHeliosLegacyToken } from '@components/common/auth/verify-helios-legacy-jwt';

async function localValidate(
  token: string,
  isCognito: boolean,
  cognitoSourceToken: string,
) {
  const emptyResult = { isValid: false, payload: null };

  if (!token) {
    return emptyResult;
  } else if (isCognito) {
    return await verifyCognitoJwt(token, cognitoSourceToken);
  } else {
    return isHeliosLegacyTokenExpired(token)
      ? emptyResult
      : { isValid: true, payload: null };
  }
}

export function useInspectorAuth(): {
  isAuthenticated: boolean;
  error: Error | null;
  isLoading: boolean;
} {
  localStorage.setItem('url-from-helios', window.location.href);
  const queryParams: URLSearchParams = useQueryParams();

  const cognitoToken =
    queryParams.get('heliosToken') ??
    queryParams.get('sicToken') ??
    localStorage.getItem('inspector-cognito-token');
  const heliosLegacyToken =
    queryParams.get('heliosLegacyToken') ??
    localStorage.getItem('inspector-legacy-token');
  const cognitoSourceToken = queryParams.get('sicToken')
    ? 'cognito-sic'
    : queryParams.get('heliosToken')
    ? 'cognito-helios'
    : localStorage.getItem('user-type') === UserType.INSPECTOR_SIC
    ? 'cognito-sic'
    : 'cognito-helios';

  // Verificar si vienen ambos tokens de Helios (heliosToken + heliosLegacyToken)
  const hasBothHeliosTokens = Boolean(
    queryParams.get('heliosToken') && queryParams.get('heliosLegacyToken'),
  );

  // Validation of Cognito token
  const {
    data: cognitoValidation,
    error: cognitoErr,
    isValidating: isCognitoValidating,
  } = useSWR(
    cognitoToken ? ['cognito', cognitoToken, cognitoSourceToken] : null,
    ([, token, source]) => {
      return localValidate(token, true, source);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10000, // Evitar validaciones duplicadas por 10 segundos
    },
  );

  // Validation of legacy token
  const {
    data: legacyValidation,
    error: legacyErr,
    isValidating: isLegacyValidating,
  } = useSWR(
    heliosLegacyToken ? ['legacy', heliosLegacyToken] : null,
    ([, token]) => localValidate(token, false, cognitoSourceToken),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10000, // Evitar validaciones duplicadas por 10 segundos
    },
  );

  // Validación remota para token legacy (si es necesario)
  const needsRemote = Boolean(
    heliosLegacyToken &&
      legacyValidation?.isValid &&
      !isHeliosLegacyTokenExpired(heliosLegacyToken),
  );

  const {
    data: remote = { isValid: false, payload: null },
    error: remoteErr,
    isValidating: isRemoteValidating,
  } = useSWR(
    needsRemote ? ['remote', heliosLegacyToken] : null,
    ([, token]) => verifyHeliosLegacyToken(token ?? ''),
    {
      revalidateOnFocus: false,
      dedupingInterval: 10000, // Evitar validaciones duplicadas por 10 segundos
    },
  );

  const isLoading =
    isCognitoValidating ||
    isLegacyValidating ||
    (needsRemote && isRemoteValidating);

  // Lógica de autenticación
  let isAuthenticated = false;
  let activeToken = cognitoToken ?? heliosLegacyToken;
  let isActiveCognitoToken = Boolean(cognitoToken);

  if (hasBothHeliosTokens) {
    // Si vienen ambos tokens de Helios, cualquiera válido permite autenticación
    const cognitoValid = Boolean(cognitoValidation?.isValid);
    const legacyValid = Boolean(
      legacyValidation?.isValid &&
        (needsRemote ? Boolean(remote?.isValid) : true),
    );

    console.log('🔍 Both Helios Tokens Validation:', {
      cognitoValid,
      legacyValid,
      needsRemote,
      remoteValid: Boolean(remote?.isValid),
      isLoading,
    });

    isAuthenticated = !isLoading && (cognitoValid || legacyValid);

    // Priorizar Cognito si ambos son válidos
    if (cognitoValid) {
      activeToken = queryParams.get('heliosToken')!;
      isActiveCognitoToken = true;
    } else if (legacyValid) {
      activeToken = queryParams.get('heliosLegacyToken')!;
      isActiveCognitoToken = false;
    }
  } else {
    // Lógica original para casos normales
    const cognitoValid = Boolean(cognitoValidation?.isValid);
    const legacyValid = Boolean(
      legacyValidation?.isValid &&
        (needsRemote ? Boolean(remote?.isValid) : true),
    );

    isAuthenticated = !isLoading && (cognitoValid || legacyValid);

    // Determinar qué token usar para la sesión (priorizar Cognito si ambos son válidos)
    if (cognitoValid) {
      activeToken = cognitoToken!;
      isActiveCognitoToken = true;
    } else if (legacyValid) {
      activeToken = heliosLegacyToken!;
      isActiveCognitoToken = false;
    }
  }

  // Persist tokens en el localStorage
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    /* Limpiar tokens no relacionados con la sesión del inspector */
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    /* Limpiar el token del tipo opuesto para evitar conflictos */
    if (isActiveCognitoToken) {
      localStorage.removeItem('inspector-legacy-token');
    } else {
      localStorage.removeItem('inspector-cognito-token');
    }

    /* Guardar tipo de usuario solo si es un token cognito */
    if (isActiveCognitoToken) {
      localStorage.setItem(
        'user-type',
        cognitoSourceToken === 'cognito-sic'
          ? UserType.INSPECTOR_SIC
          : UserType.INSPECTOR,
      );
    }

    /* Guardar tokens */
    localStorage.setItem(
      isActiveCognitoToken
        ? 'inspector-cognito-token'
        : 'inspector-legacy-token',
      activeToken ?? '',
    );
  }, [isAuthenticated, isActiveCognitoToken, activeToken, cognitoSourceToken]);

  return {
    isAuthenticated,
    isLoading,
    error: cognitoErr ?? legacyErr ?? remoteErr ?? null,
  };
}
